import { format, useT } from "@/i18n";
import {
  AnswerLine,
  EmptyState,
  OptionReview,
  PromptImage,
  PromptVideo,
  ReadOnlyCard,
  Section,
  SpeakerButton,
} from "./ReadOnlyKit";
import { ScoredPassageText } from "./ScoredPassageText";
import { COLORS, SPACING } from "./readOnlyStyles";
import { getScoreColor } from "./scoring";
import type { ReadOnlyTemplateProps } from "./types";
import { useReviewAudio } from "./useReviewAudio";

const passageRow = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: SPACING.sm,
} as const;

const passageControls = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: SPACING.xs,
  flexShrink: 0,
} as const;

const quiz = {
  display: "flex",
  flexDirection: "column",
  gap: SPACING.xs,
  padding: `${SPACING.xs}px 0`,
} as const;

const question = { fontSize: 14, fontWeight: 600, color: "#4B4B4B", margin: 0 } as const;

const missingPassage = {
  flex: 1,
  fontSize: 15,
  lineHeight: "22px",
  color: COLORS.textTertiary,
  margin: 0,
} as const;

/**
 * A past reading play: one scored take per paragraph, plus the post-reading quizzes. Paragraphs
 * the student never reached simply have no row.
 */
export function PassageReadingReadOnly({
  snapshot,
  response,
  score,
  recordingUrl,
}: ReadOnlyTemplateProps<"passageReading">) {
  const t = useT();
  const { passages, subQuizzes } = snapshot.slideData;
  const { playingUrl, toggle } = useReviewAudio();

  return (
    <ReadOnlyCard title={snapshot.name} score={score}>
      {response.paragraphs.length === 0 ? (
        <EmptyState message={t["readonly.noParagraphs"]} />
      ) : (
        response.paragraphs.map((paragraph, index) => {
          const passage = passages.find((item) => item.id === paragraph.paragraphId);
          const url = recordingUrl(paragraph.take?.recordingId);

          return (
            <Section
              key={paragraph.paragraphId}
              label={format(t["readonly.paragraph"], { number: index + 1 })}
            >
              <div style={passageRow}>
                {passage?.text ? (
                  <ScoredPassageText text={passage.text} words={paragraph.words} />
                ) : (
                  <p style={missingPassage}>{t["readonly.passageNotSet"]}</p>
                )}
                <div style={passageControls}>
                  <span style={{ fontSize: 13, color: getScoreColor(paragraph.score) }}>
                    {Math.round(paragraph.score)}
                  </span>
                  <SpeakerButton
                    label={t["readonly.myReading"]}
                    url={url}
                    isPlaying={playingUrl === url}
                    onPress={() => toggle(url)}
                    variant="student"
                  />
                </div>
              </div>
              {passage?.mediaType === "video" ? (
                <PromptVideo media={passage.video} label={t["readonly.passageVideo"]} />
              ) : (
                <PromptImage media={passage?.image} label={t["readonly.passageImage"]} />
              )}
            </Section>
          );
        })
      )}

      {response.quizzes.length > 0 && (
        <Section label={t["readonly.quiz"]}>
          {response.quizzes.map((item) => {
            const subQuiz = subQuizzes.find((candidate) => candidate.id === item.id);
            const isMultipleChoice =
              subQuiz?.questionMode === "multiple_choice" &&
              (subQuiz.options.length > 0 || !!subQuiz.correctAnswer);

            return (
              <div key={item.id} style={quiz}>
                {subQuiz?.question ? <p style={question}>{subQuiz.question}</p> : null}
                {isMultipleChoice ? (
                  <OptionReview
                    options={subQuiz.options}
                    correctAnswer={subQuiz.correctAnswer}
                    studentAnswer={item.answer}
                  />
                ) : (
                  <AnswerLine
                    studentAnswer={item.answer}
                    correctAnswer={subQuiz?.correctAnswer}
                    isCorrect={item.isCorrect}
                  />
                )}
              </div>
            );
          })}
        </Section>
      )}
    </ReadOnlyCard>
  );
}
